import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMoneyBillWave, FaSearch, FaPlus, FaTrash } from 'react-icons/fa';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import SelectInput from '../../widgets/SelectInput';
import { BankTransaction } from '../../../models/BankTransaction';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { Supplier } from '../../../models/Supplier';
import { Ledger } from '../../../models/Ledger';
import { FiscalYear } from '../../../models/FiscalYear';
import { SupplierInvoiceRestDataProvider } from '../../../data/SupplierInvoiceRestDataProvider';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { BankTransactionRestDataProvider } from '../../../data/BankTransactionRestDataProvider';

interface ManualLine {
  id: number;
  ledgerId: string;
  description: string;
  amount: string;
}

interface Props {
  transaction: BankTransaction;
  ledgers: Ledger[];
  fiscalYears: FiscalYear[];
  onClose: () => void;
  onMatched: (updatedTx: BankTransaction) => void;
}

let lineCounter = 1;

const BankTransactionSupplierPaymentModal: React.FC<Props> = ({
  transaction,
  ledgers,
  fiscalYears,
  onClose,
  onMatched,
}) => {
  const { t } = useTranslation(['common', 'screens']);

  const invoiceProvider  = useMemo(() => new SupplierInvoiceRestDataProvider(), []);
  const supplierProvider = useMemo(() => new SupplierRestDataProvider(), []);
  const txProvider       = useMemo(() => new BankTransactionRestDataProvider(), []);

  const [invoices, setInvoices]         = useState<SupplierInvoice[]>([]);
  const [suppliers, setSuppliers]       = useState<Supplier[]>([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [onlyShowLikelyInvoices, setOnlyShowLikelyInvoices] = useState(true);
  const [selected, setSelected]         = useState<Set<string>>(new Set());
  // Per-invoice credit-note tracking — auto-detected from negative totalAmountIncVat, manually togglable
  const [creditNoteIds, setCreditNoteIds] = useState<Set<string>>(new Set());
  const [costLedgerId, setCostLedgerId] = useState<string>('');
  const [manualLines, setManualLines]   = useState<ManualLine[]>([]);
  const [saving, setSaving]             = useState(false);
  const [confirmOpen, setConfirmOpen]   = useState(false);

  // Auto-detect fiscal year from transaction value date
  const autoFiscalYear = useMemo(() => {
    if (!fiscalYears.length) return null;
    const txDate = new Date(transaction.valueDate);
    return (
      fiscalYears.find(fy => txDate >= new Date(fy.start) && txDate <= new Date(fy.end))
      ?? fiscalYears[0]
    );
  }, [fiscalYears, transaction.valueDate]);

  // Load unpaid supplier invoices + suppliers on mount
  useEffect(() => {
    Promise.all([invoiceProvider.getAll(), supplierProvider.getAll()])
      .then(([allInvoices, allSuppliers]) => {
        const unpaid = allInvoices.filter(inv => !inv.isPaid);
        setInvoices(unpaid);
        setSuppliers(allSuppliers);
        // Auto-detect credit notes by negative amount
        setCreditNoteIds(new Set(unpaid.filter(inv => (inv.totalAmountIncVat ?? 0) < 0).map(inv => inv.id)));
      })
      .finally(() => setLoading(false));
  }, [invoiceProvider, supplierProvider]);

  const supplierName = (supplierId: string) =>
    suppliers.find(s => s.id === supplierId)?.name ?? '—';

  // Build a single lowercase string from all relevant transaction description fields
  const transactionDescription = useMemo(() => [
    transaction.narrative,
    transaction.narrativeRemittanceInfo,
    transaction.narrativeEndToEndReference,
    transaction.customerReference,
    transaction.supplementaryDetails,
  ].filter(Boolean).join(' ').toLowerCase(), [transaction]);

  // Filter by search term (invoice number, supplier name, or reference) and sort by invoice date ascending
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let result = q
      ? invoices.filter(
          inv =>
            inv.invoiceNumber.toLowerCase().includes(q) ||
            supplierName(inv.supplierId).toLowerCase().includes(q) ||
            inv.reference.toLowerCase().includes(q),
        )
      : [...invoices];

    // Only show invoices whose invoice number or reference appears in the transaction description
    if (onlyShowLikelyInvoices && transactionDescription) {
      result = result.filter(inv => {
        const num = inv.invoiceNumber?.trim().toLowerCase();
        const ref = inv.reference?.trim().toLowerCase();
        return (num && transactionDescription.includes(num)) ||
               (ref && transactionDescription.includes(ref));
      });
    }

    result.sort((a, b) => {
      const da = a.invoiceDate ?? '';
      const db = b.invoiceDate ?? '';
      if (da < db) return -1;
      if (da > db) return 1;
      return 0;
    });
    return result;
  }, [invoices, search, suppliers, onlyShowLikelyInvoices, transactionDescription]);

  const toggleCreditNote = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreditNoteIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Net effective total: regular invoices add to the amount owed, credit notes reduce it.
  // Both use absolute value so the type badge is the authoritative source of direction.
  const effectiveSelectedTotal = useMemo(
    () =>
      Array.from(selected).reduce((sum, id) => {
        const inv = invoices.find(i => i.id === id);
        const absAmt = Math.abs(inv?.totalAmountIncVat ?? 0);
        return creditNoteIds.has(id) ? sum - absAmt : sum + absAmt;
      }, 0),
    [selected, invoices, creditNoteIds],
  );

  // Whether any selected invoice still needs to be booked first
  const hasUnbooked = useMemo(
    () => Array.from(selected).some(id => {
      const inv = invoices.find(i => i.id === id);
      return inv && !inv.isBooked;
    }),
    [selected, invoices],
  );

  // ── Manual ledger lines ───────────────────────────────────────────────────

  const addManualLine = () =>
    setManualLines(lines => [
      ...lines,
      { id: lineCounter++, ledgerId: '', description: transaction.narrativeRemittanceInfo ?? transaction.customerReference ?? '', amount: '' },
    ]);

  const removeManualLine = (id: number) =>
    setManualLines(lines => lines.filter(l => l.id !== id));

  const updateManualLine = (id: number, field: keyof Omit<ManualLine, 'id'>, value: string) =>
    setManualLines(lines => lines.map(l => l.id === id ? { ...l, [field]: value } : l));

  const validManualLines = useMemo(
    () => manualLines.filter(l => l.ledgerId && parseFloat(l.amount) > 0),
    [manualLines],
  );
  const invalidManualLines = useMemo(
    () => manualLines.filter(l => (l.ledgerId && !parseFloat(l.amount)) || (!l.ledgerId && parseFloat(l.amount) > 0)),
    [manualLines],
  );
  const manualLinesTotal = useMemo(
    () => validManualLines.reduce((s, l) => s + parseFloat(l.amount), 0),
    [validManualLines],
  );

  // ── Balance ───────────────────────────────────────────────────────────────

  // Compare the absolute effective net (invoices + manual lines) against the bank transaction amount.
  const isBalanced   = Math.abs(Math.abs(effectiveSelectedTotal) + manualLinesTotal - transaction.amount) < 0.005;
  const costLedgerOk = !hasUnbooked || !!costLedgerId;
  const canMatch     = isBalanced && selected.size > 0 && costLedgerOk && invalidManualLines.length === 0 && !saving;
  const remaining    = transaction.amount - (Math.abs(effectiveSelectedTotal) + manualLinesTotal);

  const toggleInvoice = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleConfirmMatch = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const paymentDate = transaction.valueDate;

      // 1. Book manual ledger lines first (partial booking, no balance-check on backend).
      let manualRefNumber: string | null = null;
      if (validManualLines.length > 0 && autoFiscalYear) {
        const result = await txProvider.bookSupplementary(transaction.id, {
          fiscalYearId: autoFiscalYear.id,
          lines: validManualLines.map(l => ({
            ledgerId:    l.ledgerId,
            description: l.description,
            amount:      parseFloat(l.amount),
          })),
        });
        manualRefNumber = result.referenceNumber;
      }

      // 2. Book/match selected invoices.
      for (const id of Array.from(selected)) {
        const inv = invoices.find(i => i.id === id)!;
        const isInvCreditNote = creditNoteIds.has(id);
        if (!inv.isBooked) {
          await invoiceProvider.book(id, costLedgerId, inv.invoiceDate ?? undefined);
        }
        await invoiceProvider.bookPayment(id, paymentDate, transaction.bankAccountId ?? '', isInvCreditNote);
      }

      // 3. Build combined reference and update the transaction.
      const invoiceRefs = Array.from(selected)
        .map(id => `SUP${invoices.find(i => i.id === id)?.invoiceNumber ?? ''}`);
      const allRefs = [...(manualRefNumber ? [manualRefNumber] : []), ...invoiceRefs];
      const ledgerReferenceCode = allRefs.join(', ');

      const updatedTx: BankTransaction = { ...transaction, isSupplierPaymentMatched: true, ledgerReferenceCode };
      await txProvider.update(transaction.id, updatedTx);
      onMatched(updatedTx);
    } catch {
      // Errors surfaced via BaseRestDataProvider toast
    } finally {
      setSaving(false);
    }
  };

  const ledgerOptions = ledgers.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }));

  return (
    <>
      <PopupDialog
        open
        title={t('screens:bankTransactions.supplierPayment.title')}
        onClose={saving ? () => {} : onClose}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canMatch}
              onClick={() => setConfirmOpen(true)}
            >
              <FaMoneyBillWave style={{ marginRight: 6 }} />
              {saving
                ? t('screens:bankTransactions.supplierPayment.matching')
                : t('screens:bankTransactions.supplierPayment.match')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={saving}
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        {/* ── Transaction summary ── */}
        <div className="bank-booking-summary mb-3">
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.transactionDate')}</span>
            <span className="bank-booking-summary-value">{transaction.valueDate}</span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.counterparty')}</span>
            <span className="bank-booking-summary-value">
              {transaction.narrativeCounterpartyName || transaction.customerReference || '—'}
            </span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.amount')}</span>
            <span className="bank-booking-summary-value bank-transactions-amount--negative">
              -{transaction.amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}{transaction.currency}
            </span>
          </div>
          {(transaction.narrativeRemittanceInfo || transaction.narrative || transaction.supplementaryDetails) && (
            <div className="bank-booking-summary-row">
              <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.description')}</span>
              <span className="bank-booking-summary-value">
                {transaction.narrativeRemittanceInfo || transaction.narrative || transaction.supplementaryDetails}
              </span>
            </div>
          )}
        </div>

        {/* ── Cost ledger selector (only relevant when unbooked invoices are selected) ── */}
        <div className="mb-3">
          <label className="form-label">
            {t('screens:bankTransactions.supplierPayment.costLedger')}
            {hasUnbooked && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
          </label>
          <SelectInput
            label=""
            small
            value={costLedgerId}
            options={ledgerOptions}
            onChange={(val: string) => setCostLedgerId(val)}
            disabled={saving}
          />
          {hasUnbooked && !costLedgerId && (
            <div className="form-text text-danger">
              {t('screens:bankTransactions.supplierPayment.costLedgerRequired')}
            </div>
          )}
          {!hasUnbooked && (
            <div className="form-text text-muted">
              {t('screens:bankTransactions.supplierPayment.costLedgerNotRequired')}
            </div>
          )}
        </div>

        {/* ── Only show likely invoices ── */}
        <div className="form-check mb-2">
          <input
            id="onlyShowLikelyInvoices"
            type="checkbox"
            className="form-check-input"
            checked={onlyShowLikelyInvoices}
            onChange={e => setOnlyShowLikelyInvoices(e.target.checked)}
            disabled={saving}
          />
          <label className="form-check-label" htmlFor="onlyShowLikelyInvoices">
            {t('screens:bankTransactions.supplierPayment.onlyShowLikelyInvoices')}
          </label>
        </div>

        {/* ── Search ── */}
        <div className="bank-payment-search mb-2">
          <div className="input-group input-group-sm">
            <span className="input-group-text"><FaSearch /></span>
            <input
              type="text"
              className="form-control"
              placeholder={t('screens:bankTransactions.supplierPayment.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {/* ── Invoice list ── */}
        {loading ? (
          <div className="text-muted small">{t('common:loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-muted small">{t('screens:bankTransactions.supplierPayment.noInvoices')}</div>
        ) : (
          <div className="bank-payment-invoice-list">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th style={{ width: 110 }}>{t('screens:bankTransactions.supplierPayment.typeColumn')}</th>
                  <th>{t('screens:bankTransactions.supplierPayment.invoiceNumber')}</th>
                  <th>{t('screens:bankTransactions.supplierPayment.supplier')}</th>
                  <th>{t('screens:bankTransactions.supplierPayment.invoiceDate')}</th>
                  <th className="text-end">{t('screens:bankTransactions.supplierPayment.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                   const isSelected = selected.has(inv.id);
                   const isCN = creditNoteIds.has(inv.id);
                   return (
                     <tr
                       key={inv.id}
                       className={isSelected ? 'bank-payment-invoice-row--selected' : ''}
                       onClick={() => !saving && toggleInvoice(inv.id)}
                       style={{ cursor: saving ? 'default' : 'pointer' }}
                     >
                       <td>
                         <input
                           type="checkbox"
                           className="form-check-input"
                           checked={isSelected}
                           onChange={() => toggleInvoice(inv.id)}
                           disabled={saving}
                           onClick={e => e.stopPropagation()}
                         />
                       </td>
                       <td>
                         <span
                           className={`badge ${isCN ? 'bg-warning text-dark' : 'bg-success'}`}
                           style={{ cursor: saving ? 'default' : 'pointer', userSelect: 'none' }}
                           title={isCN
                             ? t('screens:bankTransactions.supplierPayment.toggleToInvoice')
                             : t('screens:bankTransactions.supplierPayment.toggleToCreditNote')}
                           onClick={e => !saving && toggleCreditNote(inv.id, e)}
                         >
                           {isCN
                             ? t('screens:bankTransactions.supplierPayment.typeCreditNote')
                             : t('screens:bankTransactions.supplierPayment.typeInvoice')}
                         </span>
                       </td>
                       <td className="font-monospace">{inv.invoiceNumber || inv.reference || '—'}</td>
                       <td>{supplierName(inv.supplierId)}</td>
                       <td>{inv.invoiceDate ?? '—'}</td>
                       <td className="text-end">
                         {Math.abs(inv.totalAmountIncVat).toLocaleString('nl-NL', {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2,
                         })}
                       </td>
                     </tr>
                   );
                 })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Manual ledger lines ── */}
        <div className="bank-payment-manual-lines mt-3">
          <div className="bank-payment-manual-lines-header">
            <span className="bank-payment-manual-lines-title">
              {t('screens:bankTransactions.supplierPayment.manualLines')}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={addManualLine}
              disabled={saving}
            >
              <FaPlus style={{ marginRight: 4 }} />
              {t('screens:bankTransactions.supplierPayment.addManualLine')}
            </button>
          </div>
          {manualLines.length > 0 && (
            <table className="table table-sm mt-2 mb-0">
              <thead>
                <tr>
                  <th>{t('screens:bankTransactions.supplierPayment.manualLineLedger')}</th>
                  <th>{t('screens:bankTransactions.supplierPayment.manualLineDescription')}</th>
                  <th style={{ width: 110 }} className="text-end">
                    {t('screens:bankTransactions.supplierPayment.manualLineAmount')}
                  </th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {manualLines.map(line => {
                  const isInvalid = invalidManualLines.some(l => l.id === line.id);
                  return (
                    <tr
                      key={line.id}
                      className={isInvalid ? 'bank-payment-manual-line--invalid' : ''}
                    >
                      <td>
                        <SelectInput
                          label=""
                          small
                          value={line.ledgerId}
                          options={ledgerOptions}
                          onChange={(val: string) => updateManualLine(line.id, 'ledgerId', val)}
                          disabled={saving}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={line.description}
                          onChange={e => updateManualLine(line.id, 'description', e.target.value)}
                          disabled={saving}
                          placeholder={t('screens:bankTransactions.supplierPayment.manualLineDescriptionPlaceholder')}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          value={line.amount}
                          min={0}
                          step={0.01}
                          onChange={e => updateManualLine(line.id, 'amount', e.target.value)}
                          disabled={saving}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeManualLine(line.id)}
                          disabled={saving}
                          title={t('common:remove')}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {invalidManualLines.length > 0 && (
            <div className="form-text text-danger mt-1">
              {t('screens:bankTransactions.supplierPayment.manualLineInvalid')}
            </div>
          )}
        </div>

        {/* ── Totals ── */}
        <div className="bank-payment-totals mt-2">
          <div className="bank-payment-totals-row">
            <span>{t('screens:bankTransactions.supplierPayment.selectedTotal')}</span>
            <span className={`fw-bold ${isBalanced ? 'text-success' : selected.size > 0 ? 'text-danger' : ''}`}>
              {(Math.abs(effectiveSelectedTotal) + manualLinesTotal).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {!isBalanced && (selected.size > 0 || manualLinesTotal > 0) && (
            <div className="bank-payment-totals-row text-danger small">
              <span>{t('screens:bankTransactions.supplierPayment.remaining')}</span>
              <span>{Math.abs(remaining).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>
      </PopupDialog>

      {/* ── Confirmation ── */}
      {confirmOpen && (
        <PopupDialog
          open
          title={t('screens:bankTransactions.supplierPayment.confirmTitle')}
          onClose={() => setConfirmOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-primary me-2" onClick={handleConfirmMatch}>
                <FaMoneyBillWave style={{ marginRight: 6 }} />
                {t('screens:bankTransactions.supplierPayment.confirmMatch')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmOpen(false)}>
                {t('common:cancel')}
              </button>
            </>
          }
        >
          <p>
            {t('screens:bankTransactions.supplierPayment.confirmMessage', {
              count: selected.size,
              amount: Math.abs(effectiveSelectedTotal).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            })}
          </p>
        </PopupDialog>
      )}
    </>
  );
};

export default BankTransactionSupplierPaymentModal;

